import type { WebSocketRequestDto } from '@gap/backend/websockets/models/WebSocketRequestDto'
import { toSubscriptionKey } from './SubscriptionRegistry'

const SUBSCRIPTION_ACTIONS = ['addsubscriptions', 'removesubscriptions'] as const

type SubscriptionAction = typeof SUBSCRIPTION_ACTIONS[number]

const opposingAction: Record<SubscriptionAction, SubscriptionAction> = {
   addsubscriptions: 'removesubscriptions',
   removesubscriptions: 'addsubscriptions',
}

export interface OutboxFrame {
   action: string
   data: WebSocketRequestDto
   /** Set only on frames whose sender is waiting for the server to confirm them. */
   correlationId?: string
}

/** What `add` did with the queue: how many frames it had to drop, and every frame that will never be sent. */
export interface OutboxAddResult {
   droppedFrames: number
   discarded: OutboxFrame[]
}

const isSubscriptionAction = (action: string): action is SubscriptionAction =>
   (SUBSCRIPTION_ACTIONS as readonly string[]).includes(action)

const keysOf = (frame: OutboxFrame) => new Set(frame.data.subscriptions.map(toSubscriptionKey))

/** Holds the frames waiting for an open socket, in the order they were queued. */
export default class SubscriptionOutbox {
   /** Frames held while offline, past which the oldest are dropped. */
   static readonly MAX_FRAMES = 200

   private frames: OutboxFrame[] = []

   /**
    * Queues a frame, cancelling whatever is already queued that would undo it.
    * @param frame The frame to send once the socket is open.
    * @returns How many frames were dropped to stay within `MAX_FRAMES`, and every frame that will never be sent.
    */
   add = (frame: OutboxFrame): OutboxAddResult => {
      const discarded = isSubscriptionAction(frame.action) ? this.cancelOpposing(frame) : []
      this.frames.push(frame)
      const overflow = Math.max(0, this.frames.length - SubscriptionOutbox.MAX_FRAMES)
      if (overflow > 0) {
         discarded.push(...this.frames.slice(0, overflow))
         this.frames = this.frames.slice(overflow)
      }
      return { droppedFrames: overflow, discarded }
   }

   /** Hands over every queued frame, in the order queued, leaving the outbox empty. */
   drain = (): OutboxFrame[] => {
      const drained = this.frames
      this.frames = []
      return drained
   }

   /** Puts frames that never reached the socket back at the front, keeping their order. */
   restore = (frames: OutboxFrame[]) => {
      this.frames = [...frames, ...this.frames]
   }

   /** Empties the outbox, handing back every frame that will never be sent. */
   clear = (): OutboxFrame[] => {
      const discarded = this.frames
      this.frames = []
      return discarded
   }

   /**
    * Takes the frame's own subscriptions out of whatever queued frame would undo them.
    * @param frame The frame being queued.
    * @returns The queued frames left with nothing to send.
    */
   private cancelOpposing = (frame: OutboxFrame): OutboxFrame[] => {
      const action = opposingAction[frame.action as SubscriptionAction]
      const keys = keysOf(frame)
      const discarded: OutboxFrame[] = []
      this.frames = this.frames
         .map(queued => {
            if (queued.action !== action) return queued
            const subscriptions = queued.data.subscriptions.filter(subscription => !keys.has(toSubscriptionKey(subscription)))
            return { ...queued, data: { ...queued.data, subscriptions } }
         })
         .filter(queued => {
            if (!isSubscriptionAction(queued.action) || queued.data.subscriptions.length > 0) return true
            discarded.push(queued)
            return false
         })
      return discarded
   }
}
